// homeCommunity/styled.js
import { styled } from '@mui/material/styles';
import { Tabs, Box, Button, Typography, Fab } from '@mui/material';

// Enhanced Honey Color Palette
export const honeyColors = {
  // Primary honey tones
  primary: '#F1C40F',        // Golden honey
  primaryLight: '#F4D03F',   // Light honey
  primaryDark: '#D4AC0D',    // Dark honey
  
  // Accent colors
  amber: '#FFB74D',          // Warm amber
  cream: '#FFF8E1',          // Soft cream
  honeycomb: '#D4A574',      // Honeycomb brown
  caramel: '#8D6E63',        // Deep caramel
  
  // Functional colors with honey tints
  success: '#66BB6A',        // Success green with warmth
  warning: '#FF9800',        // Warning orange (honey-friendly)
  info: '#29B6F6',          // Info blue
  error: '#EF5350',         // Error red
  
  // Neutrals with honey warmth
  background: '#FFFEF7',     // Warm white background
  surface: '#FFFFFF',        // Pure white surface
  surfaceHoney: '#FFF9E6',   // Honey-tinted surface
  border: '#F5E6B3',        // Honey border
  
  // Text colors
  textPrimary: '#3E2723',    // Dark brown text
  textSecondary: '#5D4037',  // Medium brown text
  textMuted: '#8D6E63',      // Light brown text
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientGlass: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
  
  // Shadows with honey tints
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
  shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
  shadowHeavy: '0 8px 32px rgba(212, 165, 116, 0.35)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
};

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: 'none',
  backgroundColor: honeyColors.surface,
  borderRadius: '16px 16px 0 0',
  boxShadow: honeyColors.shadowLight,
  padding: '8px 8px 0 8px',
  
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px',
    background: honeyColors.gradientPrimary,
    boxShadow: honeyColors.shadowGlow,
  },
  
  // Hide scroll buttons on desktop, show on mobile
  '& .MuiTabs-scrollButtons': {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
  },
  
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    minWidth: 'auto',
    padding: theme.spacing(1.5, 3),
    borderRadius: '12px',
    margin: '0 4px',
    color: honeyColors.textSecondary,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      backgroundColor: honeyColors.surfaceHoney,
      color: honeyColors.textPrimary,
      transform: 'translateY(-1px)',
    },
    
    '&.Mui-selected': {
      color: honeyColors.primary,
      fontWeight: 700,
      backgroundColor: honeyColors.surfaceHoney,
    },
  },
}));

export const MainContainer = styled(Box)({
  display: 'flex',
  height: '100%',
  position: 'relative',
  backgroundColor: honeyColors.background,
});

export const MobileSidebarToggle = styled(Button)({
  position: 'fixed',
  bottom: 20,
  left: 20,
  zIndex: 1100,
  borderRadius: '24px',
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  fontWeight: 600,
  boxShadow: honeyColors.shadowMedium,
  backdropFilter: 'blur(10px)',
  border: `2px solid ${honeyColors.border}`,
  
  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowHeavy,
  },
});

export const MainContent = styled(Box)({
  flexGrow: 1,
  minHeight: '100vh', // Use minHeight instead of height
  overflow: 'visible', // Keep visible to prevent scroll container
  width: '100%',
  backgroundColor: honeyColors.background,
});

export const HeaderContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  marginBottom: 48,
  padding: '0 24px',
  
  [theme.breakpoints.down('md')]: {
    padding: '0 16px',
    marginBottom: 32,
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    marginBottom: 24,
  },
}));

export const BackgroundImage = styled(Box)(({ backgroundImage, theme }) => ({
  width: '100%',
  height: 280,
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: honeyColors.shadowHeavy,
  
  [theme.breakpoints.down('md')]: {
    height: 240,
    borderRadius: '16px',
  },
  
  [theme.breakpoints.down('sm')]: {
    height: 200,
    borderRadius: '12px',
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(62, 39, 35, 0.8) 0%, rgba(62, 39, 35, 0.6) 50%, rgba(62, 39, 35, 0.4) 100%)',
    zIndex: 1,
  },
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: honeyColors.gradientGlass,
    zIndex: 1,
  },
}));

export const ContentOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  padding: '32px 40px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  
  [theme.breakpoints.down('md')]: {
    padding: '24px 32px',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '20px 24px',
  },
}));

export const LogoContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
});

export const LogoBox = styled(Box)(({ theme }) => ({
  width: 96,
  height: 96,
  borderRadius: '20px',
  overflow: 'hidden',
  border: `4px solid ${honeyColors.surface}`,
  backgroundColor: honeyColors.surface,
  boxShadow: `${honeyColors.shadowMedium}, inset 0 0 0 1px ${honeyColors.border}`,
  flexShrink: 0,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  [theme.breakpoints.down('md')]: {
    width: 80,
    height: 80,
    borderRadius: '16px',
  },
  
  [theme.breakpoints.down('sm')]: {
    width: 64,
    height: 64,
    borderRadius: '12px',
    border: `3px solid ${honeyColors.surface}`,
  },
  
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `${honeyColors.shadowHeavy}, inset 0 0 0 1px ${honeyColors.primary}`,
  },
}));

export const LogoImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const LogoPlaceholder = styled(Box)({
  width: '100%',
  height: '100%',
  background: honeyColors.gradientPrimary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: honeyColors.textPrimary,
  fontSize: '2.2rem',
  fontWeight: 800,
  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
});

export const CommunityInfoContainer = styled(Box)(({ theme }) => ({
  maxWidth: '65%',
  
  [theme.breakpoints.down('md')]: {
    maxWidth: '75%',
  },
  
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

export const CommunityTitle = styled(Typography)(({ theme }) => ({
  color: `${honeyColors.surface} !important`,
  textShadow: '0 3px 12px rgba(0,0,0,0.8)',
  marginBottom: '16px',
  fontWeight: '800 !important',
  fontSize: '2.75rem !important',
  lineHeight: '1.2 !important',
  letterSpacing: '-0.02em',
  
  [theme.breakpoints.down('md')]: {
    fontSize: '2.25rem !important',
    marginBottom: '12px',
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.875rem !important',
    marginBottom: '8px',
  },
}));

export const CommunityDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.95) !important',
  textShadow: '0 2px 8px rgba(0,0,0,0.7)',
  lineHeight: '1.5 !important',
  fontSize: '1.1rem !important',
  fontWeight: '400 !important',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem !important',
    WebkitLineClamp: 3,
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem !important',
  },
}));

export const StatsCardsContainer = styled(Box)({
  position: 'absolute',
  top: 250,
  left: 40,
  zIndex: 5,
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
});

export const StatCard = styled(Box)({
  background: `${honeyColors.surface}`,
  borderRadius: '16px',
  padding: '12px 10px',
  boxShadow: `${honeyColors.shadowMedium}, inset 0 1px 0 rgba(255,255,255,0.8)`,
  border: `2px solid ${honeyColors.border}`,
  width: '120px',
  height: '60px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backdropFilter: 'blur(10px)',
  
  '&:hover': {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: `${honeyColors.shadowHeavy}, inset 0 1px 0 rgba(255,255,255,0.9)`,
    borderColor: honeyColors.primary,
  },
  
  '&:nth-of-type(1)': {
    borderTop: `3px solid ${honeyColors.primary}`,
  },
  '&:nth-of-type(2)': {
    borderTop: `3px solid ${honeyColors.success}`,
  },
  '&:nth-of-type(3)': {
    borderTop: `3px solid ${honeyColors.warning}`,
  },
  '&:nth-of-type(4)': {
    borderTop: `3px solid ${honeyColors.info}`,
  },
});

export const StatNumber = styled(Typography)({
  lineHeight: 1,
  marginBottom: '4px',
  color: honeyColors.textPrimary,
  fontWeight: '800 !important',
  fontSize: '1.5rem !important',
  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
});

export const StatLabel = styled(Typography)({
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: '700 !important',
  fontSize: '0.7rem !important',
  color: honeyColors.textSecondary,
  textAlign: 'center',
  lineHeight: 1,
});

export const ActionButtonsContainer = styled(Box)({
  position: 'absolute',
  top: 290,
  right: 40,
  zIndex: 5,
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
});

export const ManageButton = styled(Button)({
  borderRadius: '24px',
  textTransform: 'none',
  background: honeyColors.gradientPrimary,
  boxShadow: honeyColors.shadowMedium,
  height: 52,
  paddingLeft: '32px',
  paddingRight: '32px',
  fontWeight: 700,
  fontSize: '0.95rem',
  color: honeyColors.textPrimary,
  border: `2px solid ${honeyColors.border}`,
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    boxShadow: honeyColors.shadowHeavy,
    transform: 'translateY(-2px)',
    borderColor: honeyColors.primary,
  },
});

export const MainContentArea = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(4),
  paddingTop: theme.spacing(2),
  backgroundColor: honeyColors.background,
  
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const TabContentContainer = styled(Box)(({ theme }) => ({
  marginTop: '24px',
  backgroundColor: honeyColors.surface,
  borderRadius: '0 0 16px 16px',
  boxShadow: honeyColors.shadowLight,
  padding: '32px',
  border: `1px solid ${honeyColors.border}`,
  borderTop: 'none',
  
  [theme.breakpoints.down('md')]: {
    padding: '24px',
    marginTop: '16px',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    borderRadius: '0 0 12px 12px',
  },
}));

export const OverviewTabContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
});

// Enhanced Chat and FAB components
export const ChatWindow = styled(Box)({
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 320,
  height: 420,
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: honeyColors.shadowHeavy,
  border: `2px solid ${honeyColors.border}`,
  backgroundColor: honeyColors.surface,
  backdropFilter: 'blur(20px)',
});

export const JoinFab = styled(Box)({
  // REMOVED: position: 'fixed', bottom: 24, right: 24, - No more floating!
  zIndex: 1000,
  
  '& .MuiFab-root': {
    background: honeyColors.gradientPrimary,
    color: honeyColors.textPrimary,
    boxShadow: honeyColors.shadowMedium,
    border: `2px solid ${honeyColors.border}`,
    
    '&:hover': {
      background: honeyColors.gradientHoneycomb,
      transform: 'scale(1.1)',
      boxShadow: honeyColors.shadowHeavy,
    },
  },
});

// NEW: Top Join Button - Same styling as JoinFab but without fixed positioning
export const TopJoinButton = styled(Fab)({
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  boxShadow: honeyColors.shadowMedium,
  border: `2px solid ${honeyColors.border}`,
  
  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'scale(1.1)',
    boxShadow: honeyColors.shadowHeavy,
  },
});

// Additional modern components for sections
export const SectionContainer = styled(Box)({
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  padding: '24px',
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
  },
  
  '&:hover': {
    borderColor: honeyColors.primary,
    boxShadow: honeyColors.shadowMedium,
  },
});

export const SectionTitle = styled(Typography)({
  fontSize: '1.5rem !important',
  fontWeight: '700 !important',
  color: honeyColors.textPrimary,
  marginBottom: '20px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
  },
});

export const ActionButton = styled(Button)({
  borderRadius: '16px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowMedium,
    borderColor: honeyColors.primary,
  },
});

// ─── Stats Bar ────────────────────────────────────────────────────────────────
export const StatsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: honeyColors.surface,
  border: `1px solid ${honeyColors.border}`,
  borderRadius: '16px',
  padding: '14px 24px',
  marginBottom: '20px',
  boxShadow: honeyColors.shadowLight,
  flexWrap: 'wrap',
  gap: 0,

  [theme.breakpoints.down('sm')]: {
    padding: '10px 16px',
    borderRadius: '12px',
    gap: '4px',
  },
}));

export const StatsBarItem = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '0 20px',

  '& + &': {
    borderLeft: `1px solid ${honeyColors.border}`,
  },

  [theme.breakpoints.down('sm')]: {
    flex: '0 0 calc(50% - 2px)',
    padding: '6px 10px',
    justifyContent: 'flex-start',
    '& + &': {
      borderLeft: 'none',
    },
  },
}));

export const StatsBarNumber = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.15rem',
  color: honeyColors.textPrimary,
  lineHeight: 1.1,
});

export const StatsBarLabel = styled(Typography)({
  fontSize: '0.75rem',
  color: honeyColors.textMuted,
  fontWeight: 500,
  lineHeight: 1.2,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
});